<!-- Header -->
<div class="generator-header">
  <div class="header-left">
    <div class="header-icon">
      <dojo-icon icon="vial" [size]="14" faPrefix="fas" />
    </div>
    <h2 class="header-title"><dojo-markdown-viewer [content]="panelTitle()" /></h2>
  </div>
  <dojo-icon-button icon="xmark" size="xs" [transparent]="true" (click)="closePanel.emit()" />
</div>

<!-- Accordion -->
<mat-accordion displayMode="flat" class="generator-accordion">
  <!-- ═══════════════════════════════════════════════════════════════════ -->
  <!-- Library Panel                                                      -->
  <!-- ═══════════════════════════════════════════════════════════════════ -->
  <mat-expansion-panel
    class="generator-panel"
    [expanded]="libraryExpanded()"
    (opened)="libraryExpanded.set(true)"
    (closed)="libraryExpanded.set(false)"
  >
    <mat-expansion-panel-header>
      <mat-panel-title>
        <dojo-icon icon="book-open" [size]="13" />
        Library
      </mat-panel-title>
    </mat-expansion-panel-header>

    <ng-template matExpansionPanelContent>
      <div class="library-list">
        @for (dataset of filteredDatasets(); track dataset.id) {
          <button class="library-item" (click)="onLoadDataset(dataset)">
            <div class="library-item-icon">
              <dojo-icon [icon]="dataset.icon" [size]="13" />
            </div>
            <div class="library-item-info">
              <span class="library-item-name">{{ dataset.name }}</span>
              <span class="library-item-meta">{{ dataset.description }}</span>
            </div>
            <span class="library-item-badge">n={{ getDatasetSize(dataset) }}</span>
          </button>
        } @empty {
          <div class="library-empty">No library datasets for this test type.</div>
        }
      </div>
    </ng-template>
  </mat-expansion-panel>

  <!-- ═══════════════════════════════════════════════════════════════════ -->
  <!-- Generate Panel                                                     -->
  <!-- ═══════════════════════════════════════════════════════════════════ -->
  <mat-expansion-panel
    class="generator-panel"
    [expanded]="generateExpanded()"
    (opened)="generateExpanded.set(true)"
    (closed)="generateExpanded.set(false)"
  >
    <mat-expansion-panel-header>
      <mat-panel-title>
        <dojo-icon icon="wand-magic-sparkles" [size]="13" />
        Generate
      </mat-panel-title>
    </mat-expansion-panel-header>

    <ng-template matExpansionPanelContent>
      <!-- Preview -->
      <section class="section">
        <div class="preview-card">
          <div class="preview-label">PREVIEW</div>
          <div class="preview-chart-wrapper">
            <div echarts [options]="previewChartOptions()" [initOpts]="initOpts" class="preview-chart"></div>
          </div>
          <div class="preview-metric-display">
            {{ effectMetricLabel() }}&nbsp;=&nbsp;<span class="preview-metric-value">{{ effectMetricValue() }}</span>
          </div>
        </div>
      </section>

      <!-- Effect Strength -->
      <section class="section">
        <div class="section-label">{{ effectLabel() }}</div>
        <input
          type="range"
          class="effect-slider"
          min="0"
          max="1"
          step="0.01"
          [value]="effectStrength()"
          [style.--slider-progress]="strengthSliderProgress()"
          (input)="onEffectSlider($event)"
        />
        <div class="slider-labels">
          <span>0</span>
          <span>0.5</span>
          <span>1.0</span>
        </div>
        <div class="preset-row">
          @for (preset of effectPresets; track $index) {
            <dojo-button
              [size]="'s'"
              [fill]="effectStrength() === preset ? 'outline' : 'clear'"
              [customColor]="effectStrength() === preset ? 'sky' : undefined"
              (click)="setEffectStrength(preset)"
            >
              {{ effectPresetLabels[$index] }}
            </dojo-button>
          }
        </div>
      </section>

      <!-- Sample Size -->
      <section class="section">
        <div class="section-label">
          {{ sampleSizeLabel() }}
          <span class="sample-size-value">{{ sampleSize() }}</span>
        </div>
        <input
          type="range"
          class="sample-slider"
          min="5"
          max="200"
          step="1"
          [value]="sampleSize()"
          [style.--slider-progress]="sampleSliderProgress()"
          (input)="onSampleSizeSlider($event)"
        />
        <div class="preset-row">
          @for (n of sampleSizePresets; track n) {
            <dojo-button
              [size]="'s'"
              [fill]="sampleSize() === n ? 'outline' : 'clear'"
              [customColor]="sampleSize() === n ? 'sky' : undefined"
              (click)="setSampleSize(n)"
            >
              n={{ n }}
            </dojo-button>
          }
        </div>
      </section>

      <!-- Hypothesized Mean (one-sample t only) -->
      @if (showHypothesizedMean()) {
        <section class="section">
          <div class="section-label">HYPOTHESIZED MEAN (&mu;&#8320;)</div>
          <input
            type="number"
            class="mean-input"
            [value]="hypothesizedMean()"
            (input)="onHypothesizedMeanInput($event)"
          />
        </section>
      }

      <!-- Group Size Toggle (two-sample t only) -->
      @if (showGroupSizeToggle()) {
        <section class="section">
          <div class="section-label">GROUP SIZES</div>
          <mat-button-toggle-group
            class="custom-toggle-group size-xs group-size-toggle"
            hideSingleSelectionIndicator
            [value]="equalGroupSizes() ? 'equal' : 'unequal'"
            (change)="setEqualGroupSizes($event.value === 'equal')"
          >
            <mat-button-toggle value="equal">Equal</mat-button-toggle>
            <mat-button-toggle value="unequal">Unequal</mat-button-toggle>
          </mat-button-toggle-group>
        </section>
      }

      <!-- Contingency Table Size (chi-squared independence only) -->
      @if (showContingencySize()) {
        <section class="section">
          <div class="section-label">TABLE SIZE</div>
          <div class="preset-row">
            @for (opt of contingencySizeOptions; track opt.label) {
              <dojo-button
                [size]="'s'"
                [fill]="contingencyRows() === opt.rows && contingencyCols() === opt.cols ? 'outline' : 'clear'"
                [customColor]="contingencyRows() === opt.rows && contingencyCols() === opt.cols ? 'sky' : undefined"
                (click)="setContingencySize(opt.rows, opt.cols)"
              >
                {{ opt.label }}
              </dojo-button>
            }
          </div>
        </section>
      }

      <!-- GOF Categories (chi-squared GOF only) -->
      @if (showGofCategories()) {
        <section class="section">
          <div class="section-label">CATEGORIES</div>
          <div class="preset-row">
            @for (k of gofCategoryOptions; track k) {
              <dojo-button
                [size]="'s'"
                [fill]="gofCategories() === k ? 'outline' : 'clear'"
                [customColor]="gofCategories() === k ? 'sky' : undefined"
                (click)="setGofCategories(k)"
              >
                {{ k }}
              </dojo-button>
            }
          </div>
        </section>
      }

      <!-- Decimal Places -->
      <section class="section">
        <div class="section-label mb-1">DECIMAL PLACES</div>
        <div class="preset-row">
          @for (dp of decimalPlacesOptions; track dp) {
            <dojo-button
              [size]="'s'"
              [fill]="decimalPlaces() === dp ? 'outline' : 'clear'"
              [customColor]="decimalPlaces() === dp ? 'sky' : undefined"
              (click)="setDecimalPlaces(dp)"
            >
              {{ dp }}
            </dojo-button>
          }
        </div>
      </section>

      <!-- Generate Button -->
      <div class="generate-action">
        <dojo-button [size]="'l'" [fill]="'solid'" customColor="sky" (click)="onGenerate()">
          <dojo-icon icon="wand-magic-sparkles" [size]="14" faPrefix="fas" />
          {{ generateButtonLabel() }}
        </dojo-button>
      </div>
    </ng-template>
  </mat-expansion-panel>
</mat-accordion>
